import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Disclosure,
  EmptyState,
  ListRow,
  Skeleton,
} from "../../../design-v2";
import {
  getCurrentCycleSources,
  getPastCycleSourceDetail,
  getPastCycleSources,
  getWeeklyPlanSources,
} from "../api/workoutLogApi";
import {
  countLabel,
  cycleDateRange,
  defaultExpandedWeek,
  projectionLabel,
  startPathForTarget,
  weekSummary,
  weekdayName,
  workoutMetricParts,
} from "../domain/chooseWorkout";
import useConnectivity from "../hooks/useConnectivity";

const TABS = Object.freeze([
  { id: "current", label: "Current Cycle" },
  { id: "past", label: "Past Cycles" },
  { id: "weekly", label: "Weekly Plans" },
]);

function deviceTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (_error) {
    return undefined;
  }
}

// One source read. The result is kept per `key`, so switching tabs never
// refetches, and a response that arrives after its key changed (another tab,
// another past cycle, a retry) is dropped instead of replacing newer state.
function useSourceRequest(key, load, { enabled, online }) {
  const [state, setState] = useState({ key: null, status: "idle", data: null });
  const [attempt, setAttempt] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled || !online || key == null) return undefined;
    let active = true;
    setState({ key, status: "loading", data: null });
    loadRef.current(key).then(
      (data) => { if (active) setState({ key, status: "success", data }); },
      () => { if (active) setState({ key, status: "error", data: null }); }
    );
    return () => { active = false; };
  }, [attempt, enabled, key, online]);

  let status = state.key === key ? state.status : "loading";
  if (!enabled || key == null) status = "idle";
  else if (!online) status = "offline";
  return {
    status,
    data: status === "success" ? state.data : null,
    retry: () => setAttempt((value) => value + 1),
  };
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M19 12H5M11 6l-6 6 6 6" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg className="lz-workout-choose__chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="lz-workout-choose__check" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

// WAI-ARIA tabs (no shared Tabs primitive exists): roving tabindex with
// Arrow keys, Home and End, styled as the Design V2 segmented control.
function SourceTabs({ value, onChange }) {
  const tabRefs = useRef({});

  function onKeyDown(event, index) {
    const last = TABS.length - 1;
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    onChange(TABS[next].id);
    tabRefs.current[TABS[next].id]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label="Workout sources"
      className="lz-v2-segmented-control lz-workout-choose__tabs"
    >
      {TABS.map((tab, index) => {
        const selected = tab.id === value;
        return (
          <button
            key={tab.id}
            ref={(node) => { tabRefs.current[tab.id] = node; }}
            type="button"
            role="tab"
            id={`choose-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`choose-panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            className={`lz-v2-segmented-control__option${selected ? " lz-v2-segmented-control__option--selected" : ""}`}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {selected ? <span className="lz-workout-choose__tab-dot" aria-hidden="true" /> : null}
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function LoadingState({ label }) {
  return (
    <div className="lz-workout-choose__loading">
      <p role="status" className="lz-v2-visually-hidden">{label}</p>
      {[0, 1, 2].map((index) => (
        <Skeleton key={index} className="lz-workout-choose__skeleton" height={64} />
      ))}
    </div>
  );
}

function ErrorState({ onRetry }) {
  return (
    <Card tone="muted" className="lz-workout-choose__state">
      <p role="alert">Workouts couldn’t be loaded.</p>
      <Button variant="secondary" size="sm" onClick={onRetry}>Retry</Button>
    </Card>
  );
}

function OfflineState() {
  return (
    <Card tone="muted" className="lz-workout-choose__state">
      <p role="status"><strong>Needs a connection</strong></p>
      <p className="lz-workout-muted">Your workouts load again when you’re back online.</p>
    </Card>
  );
}

// Loading, offline and error share one treatment in every panel; `children`
// renders only with a successful response.
function RequestState({ request, loadingLabel, children }) {
  if (request.status === "offline") return <OfflineState />;
  if (request.status === "error") return <ErrorState onRetry={request.retry} />;
  if (request.status !== "success") return <LoadingState label={loadingLabel} />;
  return children(request.data);
}

// One secondary line of context and metrics ("Today · Tuesday · 6 exercises ·
// ~55 min"). Parts wrap whole on narrow phones; each keeps its trailing
// separator so a wrapped line never starts with one.
function MetaLine({ parts }) {
  if (!parts.length) return null;
  return (
    <span className="lz-workout-choose__meta">
      {parts.map((part, index) => (
        <span
          key={part.key}
          className={`lz-workout-choose__meta-part${part.tone ? ` lz-workout-choose__meta-part--${part.tone}` : ""}`}
        >
          {part.text}
          {index < parts.length - 1 ? <span className="lz-workout-choose__meta-sep"> ·</span> : null}
        </span>
      ))}
    </span>
  );
}

function metricParts(source) {
  return workoutMetricParts(source).map((text, index) => ({ key: `metric-${index}`, text }));
}

// Hierarchy: workout title, then context/metrics, then an optional small
// completion status, then the lowest-priority classification (always the
// server projection's canonical copy).
function SourceRow({ title, meta, completed = false, projection, highlighted = false, onStart }) {
  return (
    <ListRow
      className={`lz-workout-choose__row${highlighted ? " lz-workout-choose__row--today" : ""}`}
      title={title}
      description={(
        <>
          <MetaLine parts={meta} />
          {completed ? (
            <span className="lz-workout-choose__status">
              <CheckIcon />
              Completed
            </span>
          ) : null}
          {projection ? (
            <span className={`lz-workout-choose__projection lz-workout-choose__projection--${projection.tone}`}>
              {projection.text}
            </span>
          ) : null}
        </>
      )}
      trailing={<ChevronIcon />}
      onClick={onStart}
    />
  );
}

function GroupTitle({ title, badge, summary, summaryDone = false }) {
  return (
    <span className="lz-workout-choose__group-title">
      <span className="lz-workout-choose__group-name">{title}</span>
      {badge ? <span className="lz-workout-choose__badge">{badge}</span> : null}
      {summary ? (
        <span className={`lz-workout-choose__group-summary${summaryDone ? " lz-workout-choose__group-summary--done" : ""}`}>
          {summaryDone ? <CheckIcon /> : null}
          {summary}
        </span>
      ) : null}
    </span>
  );
}

// Groups a person opened or closed; `null` means the default (one open group).
function isGroupOpen(expanded, defaultId, id) {
  return expanded ? expanded.has(id) : id === defaultId;
}

function toggleGroup(expanded, defaultId, id, open) {
  const next = new Set(expanded || (defaultId == null ? [] : [defaultId]));
  if (open) next.add(id);
  else next.delete(id);
  return next;
}

// Temporal context first, then the server's metrics (display only).
function occurrenceMeta(occurrence) {
  return [
    occurrence.temporalStatus === "TODAY" ? { key: "today", text: "Today", tone: "today" } : null,
    { key: "day", text: weekdayName(occurrence.operationalDate) },
    ...metricParts(occurrence),
  ].filter((part) => part?.text);
}

// Display only, from the server's `completed` flags.
function isWeekComplete(week) {
  return week.occurrences.length > 0 && week.occurrences.every((occurrence) => occurrence.completed);
}

function CurrentCyclePanel({ request, expanded, onExpandedChange, onStart }) {
  return (
    <RequestState request={request} loadingLabel="Loading your current cycle">
      {(data) => {
        if (!data?.cycle) {
          return (
            <EmptyState
              title="No current cycle"
              body="Workouts from your current program appear here."
            />
          );
        }
        const defaultWeek = defaultExpandedWeek(data);
        return (
          <>
            <Card className="lz-workout-choose__banner">
              <span className="lz-workout-choose__banner-copy">
                <strong>{data.cycle.name}</strong>
                <span>{data.cycle.temporalStatus === "upcoming" ? "Upcoming cycle" : "Current Cycle"}</span>
              </span>
              {data.cycle.currentWeekNumber ? (
                <span className="lz-workout-choose__banner-week">Week {data.cycle.currentWeekNumber}</span>
              ) : null}
            </Card>
            {data.weeks.length ? data.weeks.map((week) => (
              <Disclosure
                key={week.weekNumber}
                className={`lz-workout-choose__group${week.weekNumber === data.cycle.currentWeekNumber ? " lz-workout-choose__group--current" : ""}`}
                open={isGroupOpen(expanded, defaultWeek, week.weekNumber)}
                onOpenChange={(open) => onExpandedChange(toggleGroup(expanded, defaultWeek, week.weekNumber, open))}
                title={(
                  <GroupTitle
                    title={`Week ${week.weekNumber}`}
                    badge={week.weekNumber === data.cycle.currentWeekNumber ? "Current" : null}
                    summary={weekSummary(week.occurrences)}
                    summaryDone={isWeekComplete(week)}
                  />
                )}
              >
                <div className="lz-workout-choose__rows">
                  {week.occurrences.map((occurrence) => (
                    <SourceRow
                      key={occurrence.occurrenceKey}
                      title={occurrence.workoutName}
                      meta={occurrenceMeta(occurrence)}
                      completed={occurrence.completed}
                      projection={projectionLabel(occurrence.projectionIfStartedNow, occurrence.weekNumber)}
                      highlighted={occurrence.temporalStatus === "TODAY" && !occurrence.completed}
                      onStart={() => onStart(occurrence.startTarget, { occurrence })}
                    />
                  ))}
                </div>
              </Disclosure>
            )) : (
              <EmptyState title="No workouts in this cycle yet" />
            )}
          </>
        );
      }}
    </RequestState>
  );
}

function SourceWorkoutRows({ workouts, onStart }) {
  return (
    <div className="lz-workout-choose__rows">
      {workouts.map((workout) => (
        <SourceRow
          key={workout.workoutId}
          title={workout.name}
          meta={metricParts(workout)}
          projection={projectionLabel(workout.projectionIfStartedNow)}
          onStart={() => onStart(workout.startTarget)}
        />
      ))}
    </div>
  );
}

function PastCyclesPanel({
  listRequest,
  detailRequest,
  selectedCycleId,
  expandedWeek,
  onExpandedWeekChange,
  onSelectCycle,
  onBack,
  onStart,
}) {
  const detailHeadingRef = useRef(null);
  const listRef = useRef(null);
  const lastSelectedRef = useRef(null);

  useEffect(() => {
    if (selectedCycleId) {
      lastSelectedRef.current = selectedCycleId;
      detailHeadingRef.current?.focus();
    } else if (lastSelectedRef.current) {
      // Back returns focus to the cycle that was opened.
      [...(listRef.current?.querySelectorAll("[data-cycle-id]") || [])]
        .find((row) => row.dataset.cycleId === lastSelectedRef.current)
        ?.focus();
    }
  }, [selectedCycleId]);

  if (selectedCycleId) {
    const listed = listRequest.data?.cycles?.find((cycle) => cycle.cycleId === selectedCycleId);
    const cycle = detailRequest.data?.cycle || listed || null;
    return (
      <>
        <button type="button" className="lz-workout-choose__back" onClick={onBack} aria-label="Back to past cycles">
          <BackIcon />
          <span aria-hidden="true">Past cycles</span>
        </button>
        <div className="lz-workout-choose__detail-head">
          <h2 ref={detailHeadingRef} tabIndex={-1}>{cycle?.name || "Past cycle"}</h2>
          {cycle ? <p>{cycleDateRange(cycle.startDate, cycle.endDate)}</p> : null}
        </div>
        <RequestState request={detailRequest} loadingLabel="Loading this cycle’s workouts">
          {(data) => (data.weeks.length ? data.weeks.map((week) => (
            <Disclosure
              key={week.weekNumber}
              className="lz-workout-choose__group"
              open={isGroupOpen(expandedWeek, data.weeks[0].weekNumber, week.weekNumber)}
              onOpenChange={(open) => onExpandedWeekChange(
                toggleGroup(expandedWeek, data.weeks[0].weekNumber, week.weekNumber, open)
              )}
              title={<GroupTitle title={`Week ${week.weekNumber}`} summary={countLabel(week.workouts.length, "workout")} />}
            >
              <SourceWorkoutRows workouts={week.workouts} onStart={onStart} />
            </Disclosure>
          )) : <EmptyState title="No available workouts in this cycle." />)}
        </RequestState>
      </>
    );
  }

  return (
    <RequestState request={listRequest} loadingLabel="Loading your past cycles">
      {(data) => (data.cycles.length ? (
        <div className="lz-workout-choose__rows" ref={listRef}>
          {data.cycles.map((cycle) => (
            <ListRow
              key={cycle.cycleId}
              data-cycle-id={cycle.cycleId}
              className="lz-workout-choose__row lz-workout-choose__row--cycle"
              title={cycle.name}
              description={cycleDateRange(cycle.startDate, cycle.endDate)}
              trailing={<ChevronIcon />}
              onClick={() => onSelectCycle(cycle.cycleId)}
            />
          ))}
        </div>
      ) : <EmptyState title="No past cycles yet." />)}
    </RequestState>
  );
}

function WeeklyPlansPanel({ request, expandedPlan, onExpandedPlanChange, onStart }) {
  return (
    <RequestState request={request} loadingLabel="Loading your weekly plans">
      {(data) => (data.weeklyPlans.length ? data.weeklyPlans.map((plan) => (
        <Disclosure
          key={plan.weeklyPlanParentId}
          className="lz-workout-choose__group"
          open={isGroupOpen(expandedPlan, data.weeklyPlans[0].weeklyPlanParentId, plan.weeklyPlanParentId)}
          onOpenChange={(open) => onExpandedPlanChange(toggleGroup(
            expandedPlan,
            data.weeklyPlans[0].weeklyPlanParentId,
            plan.weeklyPlanParentId,
            open
          ))}
          title={<GroupTitle title={plan.name} summary={countLabel(plan.workouts.length, "workout")} />}
        >
          <SourceWorkoutRows workouts={plan.workouts} onStart={onStart} />
        </Disclosure>
      )) : <EmptyState title="No weekly plans available." />)}
    </RequestState>
  );
}

export default function ChooseWorkoutScreen({ enabled }) {
  const navigate = useNavigate();
  const { online } = useConnectivity();
  const [tab, setTab] = useState("current");
  const [visited, setVisited] = useState(() => new Set(["current"]));
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  // Open groups per list; `null` = the default (current week / first group).
  const [currentExpanded, setCurrentExpanded] = useState(null);
  const [pastExpanded, setPastExpanded] = useState(null);
  const [weeklyExpanded, setWeeklyExpanded] = useState(null);
  const [timezone] = useState(deviceTimezone);

  const current = useSourceRequest(
    "current",
    () => getCurrentCycleSources(timezone),
    { enabled: enabled && visited.has("current"), online }
  );
  const pastList = useSourceRequest(
    "past",
    () => getPastCycleSources(timezone),
    { enabled: enabled && visited.has("past"), online }
  );
  const pastDetail = useSourceRequest(
    selectedCycleId,
    (cycleId) => getPastCycleSourceDetail(cycleId, timezone),
    { enabled: enabled && Boolean(selectedCycleId), online }
  );
  const weekly = useSourceRequest(
    "weekly",
    () => getWeeklyPlanSources(),
    { enabled: enabled && visited.has("weekly"), online }
  );

  function selectTab(id) {
    setTab(id);
    setVisited((previous) => (previous.has(id) ? previous : new Set([...previous, id])));
  }

  function start(target, state) {
    const path = startPathForTarget(target);
    if (!path) return;
    navigate(path, state ? { state } : undefined);
  }

  if (!enabled) {
    return (
      <section className="lz-workout-centered">
        <Card>
          <p>Workout Log is not currently available.</p>
          <Button variant="secondary" onClick={() => navigate("/train")}>Back to Train</Button>
        </Card>
      </section>
    );
  }

  return (
    <section className="lz-workout-choose">
      <header className="lz-workout-choose__header">
        <button
          type="button"
          className="lz-workout-choose__icon-button"
          aria-label="Back to Train"
          onClick={() => navigate("/train")}
        >
          <BackIcon />
        </button>
        <h1>Choose Workout</h1>
        <p>Pick any workout you want to do.</p>
        <SourceTabs value={tab} onChange={selectTab} />
      </header>

      <div
        role="tabpanel"
        id="choose-panel-current"
        aria-labelledby="choose-tab-current"
        hidden={tab !== "current"}
        className="lz-workout-choose__panel"
      >
        <CurrentCyclePanel
          request={current}
          expanded={currentExpanded}
          onExpandedChange={setCurrentExpanded}
          onStart={start}
        />
      </div>
      <div
        role="tabpanel"
        id="choose-panel-past"
        aria-labelledby="choose-tab-past"
        hidden={tab !== "past"}
        className="lz-workout-choose__panel"
      >
        <p className="lz-workout-choose__note">Workouts from past cycles are logged as Extra Workouts.</p>
        <PastCyclesPanel
          listRequest={pastList}
          detailRequest={pastDetail}
          selectedCycleId={selectedCycleId}
          expandedWeek={pastExpanded}
          onExpandedWeekChange={setPastExpanded}
          onSelectCycle={(cycleId) => {
            setPastExpanded(null);
            setSelectedCycleId(cycleId);
          }}
          onBack={() => setSelectedCycleId(null)}
          onStart={start}
        />
      </div>
      <div
        role="tabpanel"
        id="choose-panel-weekly"
        aria-labelledby="choose-tab-weekly"
        hidden={tab !== "weekly"}
        className="lz-workout-choose__panel"
      >
        <p className="lz-workout-choose__note">Workouts from Weekly Plans are logged as Extra Workouts.</p>
        <WeeklyPlansPanel
          request={weekly}
          expandedPlan={weeklyExpanded}
          onExpandedPlanChange={setWeeklyExpanded}
          onStart={start}
        />
      </div>
    </section>
  );
}
